import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartContainer } from "@volca/ui/components/ui/chart";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { CATEGORY_COLORS } from "@volca/ui/tokens/chart-colors";
import { dayjs } from "@/core/utils/date";
import { useReportCurrency } from "@/profitability/currency";
import { MISSING_LABEL } from "@/profitability/missing";
import type { ReportColumn } from "@/profitability/types";
import { useElementWidth } from "./use-element-width";
import { columnLabel, compactReportValue, formatReportValue } from "./utils";

const COLORS = CATEGORY_COLORS;
const TICK = { fontSize: 12, fill: "var(--subtle)" } as const;
const MAX_TICK = 18;
const MAX_TICK_NARROW = 13;
/** Ticks this short all fit flat, however many there are. */
const SHORT_TICK = 6;
/** Above this many categories the labels tilt so they all still fit. */
const ANGLE_FROM = 6;
/** About how wide one character of a 12px tick is. */
const TICK_CHAR_WIDTH = 7;
/** What the plot gives up to the y axis, the margins and the container padding. */
const PLOT_INSET = 56 + 16 + 16;
/** A pricebook item's full name belongs in the tooltip, not under a bar. */
const shortLabel = (value: string, limit: number) =>
  value.length > limit ? `${value.slice(0, limit - 1)}…` : value;
// A chart of many slices would otherwise push its legend outside the card.
const LEGEND_STYLE = {
  beside: { maxHeight: "84%", overflowY: "auto", fontSize: 12 },
  below: { maxHeight: "34%", overflowY: "auto", fontSize: 12 },
} as const;

const CHART_NAME = {
  bar: "Bar chart",
  line: "Line chart",
  pie: "Pie chart",
} as const;

export const ReportChart = ({
  type,
  rows,
  column,
  measure,
  labelColumn,
  narrow = false,
}: {
  type: "bar" | "line" | "pie";
  rows: { label: string; value: number | null }[];
  column: ReportColumn;
  /** What the value is called in the tooltip and the legend. */
  measure: string;
  /** The field the chart is broken down by, which names each point. */
  labelColumn?: ReportColumn;
  /** A chart in a narrow card keeps its legend below, not beside, the plot. */
  narrow?: boolean;
}) => {
  const currency = useReportCurrency();
  const [chartRef, chartWidth] = useElementWidth<HTMLDivElement>();
  const hasNegativeValues = rows.some(
    (row) => row.value !== null && row.value < 0,
  );
  if (
    type === "pie" &&
    !rows.some((row) => row.value !== null && row.value !== 0)
  )
    return (
      <Empty className={emptyStateVariants({ layout: "collection" })}>
        <EmptyHeader>
          <EmptyDescription>
            No nonzero values for this pie chart.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  const chartType = type === "pie" && hasNegativeValues ? "bar" : type;
  // Rows whose date is missing or unparseable are grouped under one honest
  // label. dayjs formats those as "Invalid Date", which reads as a bug in the
  // chart rather than as what it is: jobs the source gave no date for.
  const NO_DATE = "No date";
  const formatDate = (value: string, pattern: string) => {
    const date = dayjs(value);
    return date.isValid() ? date.format(pattern) : NO_DATE;
  };
  const pointName = (label: string) =>
    labelColumn?.type === "date" ? formatDate(label, "MMM D, YYYY") : label;
  const summary = `${CHART_NAME[chartType]} of ${measure}${
    labelColumn ? ` by ${columnLabel(labelColumn)}` : ""
  }: ${rows
    .map(
      (row) =>
        `${pointName(row.label)} ${
          row.value === null
            ? MISSING_LABEL.toLowerCase()
            : formatReportValue(row.value, column, currency)
        }`,
    )
    .join(", ")}`;
  const a11y = { role: "img", "aria-label": summary } as const;
  const tooltip = (
    <Tooltip
      separator=": "
      formatter={(value) => [
        // A point without a value is missing, not zero (FIN-004).
        value === null || value === undefined
          ? MISSING_LABEL
          : formatReportValue(Number(value), column, currency),
        measure,
      ]}
      labelFormatter={(value) =>
        labelColumn?.type === "date"
          ? formatDate(String(value), "ddd, MMM D, YYYY")
          : String(value)
      }
    />
  );
  // Every category is named. Past a handful they are angled rather than
  // dropped, because a bar with no label underneath says nothing.
  const limit = narrow ? MAX_TICK_NARROW : MAX_TICK;
  // A week of dates reads as days, not as seven repetitions of the year. The
  // tooltip still names the day in full.
  const weekdays = labelColumn?.type === "date" && rows.length <= 8;
  const tick = (value: string) =>
    labelColumn?.type === "date"
      ? formatDate(value, weekdays ? "ddd" : "MMM D")
      : shortLabel(String(value), limit);
  // A bar per category earns a colour per category, the same ten the pie uses, so a
  // breakdown reads as a set of things rather than one repeated block. A date axis
  // keeps one colour: time is a continuum, and thirty rotating hues down a month would
  // say that each day belongs to a different group.
  const byCategory = labelColumn?.type !== "date";
  const dateAxis = labelColumn?.type === "date";
  const longestTick = Math.max(...rows.map((row) => tick(row.label).length), 0);
  // A phone or a narrow card can't fit even a few long labels side by side, so
  // they also tilt once they would run into their neighbours.
  const slotWidth = (chartWidth - PLOT_INSET) / Math.max(rows.length, 1);
  const crowded =
    chartWidth > 0 && longestTick * TICK_CHAR_WIDTH + 8 > slotWidth;
  const angled =
    !dateAxis &&
    ((rows.length > ANGLE_FROM && longestTick > SHORT_TICK) || crowded);
  const axes = (
    <>
      <CartesianGrid vertical={false} />
      <XAxis
        dataKey="label"
        tickFormatter={(value) => tick(String(value))}
        tick={TICK}
        tickLine={false}
        axisLine={false}
        interval={dateAxis ? "preserveStartEnd" : 0}
        minTickGap={8}
        angle={angled ? -35 : 0}
        textAnchor={angled ? "end" : "middle"}
        height={angled ? 86 : 24}
        tickMargin={angled ? 4 : 8}
      />
      <YAxis
        width={56}
        tick={TICK}
        tickFormatter={(value) => compactReportValue(value, column, currency)}
        tickLine={false}
        axisLine={false}
      />
      {tooltip}
    </>
  );
  return (
    <Stack ref={chartRef} gap={0} align="stretch" className="min-h-0 flex-1">
      {type === "pie" && hasNegativeValues && (
        <Text variant="caption" tone="muted" className="px-4">
          Negative values are shown in a bar chart.
        </Text>
      )}
      <ChartContainer
        className="aspect-auto h-full min-h-0 w-full px-2 pb-3"
        config={{
          value: { label: measure, color: COLORS[0] },
        }}
      >
        {chartType === "pie" ? (
          <PieChart {...a11y}>
            {tooltip}
            <Pie
              data={rows}
              dataKey="value"
              nameKey="label"
              innerRadius="45%"
              outerRadius="78%"
              paddingAngle={1}
            >
              {rows.map((row, index) => (
                <Cell key={row.label} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Legend
              verticalAlign={narrow ? "bottom" : "middle"}
              layout={narrow ? "horizontal" : "vertical"}
              align={narrow ? "center" : "right"}
              iconType="circle"
              iconSize={8}
              wrapperStyle={LEGEND_STYLE[narrow ? "below" : "beside"]}
            />
          </PieChart>
        ) : chartType === "bar" ? (
          <BarChart
            {...a11y}
            data={rows}
            margin={{ left: angled ? 24 : 4, right: 12, bottom: 0 }}
          >
            {axes}
            <Bar dataKey="value" maxBarSize={72} radius={[4, 4, 0, 0]}>
              {rows.map((row, index) => (
                <Cell
                  key={row.label}
                  fill={byCategory ? COLORS[index % COLORS.length] : COLORS[0]}
                />
              ))}
            </Bar>
          </BarChart>
        ) : (
          <LineChart
            {...a11y}
            data={rows}
            margin={{ left: angled ? 24 : 4, right: 12, bottom: 0 }}
          >
            {axes}
            <Line
              dataKey="value"
              stroke="var(--color-value)"
              strokeWidth={2}
              dot={rows.length < 50}
              connectNulls={false}
            />
          </LineChart>
        )}
      </ChartContainer>
    </Stack>
  );
};
